"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, ArrowRight, Mic, Lock, Crown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import AudioWave from "@/components/ui/audio-wave";
import { ExampleShadowButton } from "@/components/course/ExampleShadowButton";
import { ExampleCompletionIndicator } from "@/components/course/ExampleCompletionIndicator";
import { SpinnerIcon } from "@/components/ui/SpinnerIcon";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { useI18n } from "@/lib/i18n";
import { useUser } from "@/lib/context/user-context";
import { useUsage } from "@/contexts/usage-context";
import { FreePlanLimitDialog } from "@/components/course/FreePlanLimitDialog";

export type Example = {
  english: string;
  chinese?: string;
  audioUrl?: string;
  sequence?: number;
};

interface IdiomDetailClientProps {
  slug: string;
  numericId: number;
  courseTitle: string;
  cleanedTitle: string;
  data: Record<string, any> | null;
  examples: Example[];
  prevId: number | null;
  nextId: number | null;
}

export default function IdiomDetailClient({
  slug,
  numericId,
  courseTitle,
  cleanedTitle,
  data,
  examples,
  prevId,
  nextId,
}: IdiomDetailClientProps) {
  const { t } = useI18n() as any;
  const { usageInfo, loading: usageLoading } = useUsage();
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const courseSlugKey = slug === "most-common-american-idioms" ? "idioms" : "default";
  const localizedCourseTitle = courseSlugKey === "idioms" ? t("course.detail.courses.idioms.title") : courseTitle;

  const isRestrictedCourse = slug === "most-common-american-idioms";
  const isFreePlan = !usageLoading && usageInfo?.planType === 'free';
  const lockedAccess = isRestrictedCourse && isFreePlan && numericId > 10;
  const waitForUsage = isRestrictedCourse && usageLoading;

  useEffect(() => {
    if (lockedAccess) {
      setShowLimitDialog(true);
    }
  }, [lockedAccess]);

  return (
    <AuthGuard
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <SpinnerIcon size={32} color="hsl(var(--muted-foreground))" />
        </div>
      }
    >
      <FreePlanLimitDialog
        open={showLimitDialog}
        onOpenChange={setShowLimitDialog}
        lockedTitle={cleanedTitle}
      />
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb
          slug={slug}
          courseTitle={localizedCourseTitle}
          cleanedTitle={cleanedTitle}
          numericId={numericId}
          t={t}
        />

        {waitForUsage ? (
          <div className="flex min-h-[320px] items-center justify-center">
            <SpinnerIcon size={32} color="hsl(var(--primary))" />
          </div>
        ) : lockedAccess ? (
          <LockedCourseState
            slug={slug}
            numericId={numericId}
            cleanedTitle={cleanedTitle}
            t={t}
            onShowDialog={() => setShowLimitDialog(true)}
          />
        ) : data ? (
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
            <Sidebar
              slug={slug}
              numericId={numericId}
              data={data}
              examplesCount={examples.length}
              prevId={prevId}
              nextId={nextId}
              t={t}
            />
            <MainContent
              slug={slug}
              numericId={numericId}
              cleanedTitle={cleanedTitle}
              data={data}
              examples={examples}
              prevId={prevId}
              nextId={nextId}
              t={t}
            />
          </div>
        ) : (
          <EmptyState t={t} />
        )}
      </div>
    </AuthGuard>
  );
}

function Breadcrumb({
  slug,
  courseTitle,
  cleanedTitle,
  numericId,
  t,
}: {
  slug: string;
  courseTitle: string;
  cleanedTitle: string;
  numericId: number;
  t: (key: string, fallback?: string) => string;
}) {
  return (
    <div className="text-sm text-muted-foreground mb-4">
      <Link href="/course" className="hover:underline">
        {t("course.idiomDetail.breadcrumb.courses")}
      </Link>
      <span className="mx-2">/</span>
      <Link href={`/course/${slug}`} className="hover:underline">
        {courseTitle}
      </Link>
      <span className="mx-2">/</span>
      <span>{cleanedTitle || `#${numericId}`}</span>
    </div>
  );
}

function Sidebar({
  slug,
  numericId,
  data,
  examplesCount,
  prevId,
  nextId,
  t,
}: {
  slug: string;
  numericId: number;
  data: Record<string, any>;
  examplesCount: number;
  prevId: number | null;
  nextId: number | null;
  t: (key: string, fallback?: string) => string;
}) {
  return (
    <Card className="h-fit lg:sticky lg:top-20 order-2 lg:order-1 rounded-2xl">
      <CardContent className="p-4 space-y-3 rounded-2xl">
        <div className="text-xs text-muted-foreground">{t("course.idiomDetail.sidebar.idiomLabel")}</div>
        <div className="text-lg font-semibold leading-tight">#{numericId}</div>
        {data?.difficulty && (
          <div>
            <div className="text-xs text-muted-foreground mb-1">{t("course.idiomDetail.sidebar.difficulty")}</div>
            <Badge variant="secondary" className="capitalize">
              {data.difficulty}
            </Badge>
          </div>
        )}
        <div>
          <div className="text-xs text-muted-foreground mb-1">{t("course.idiomDetail.sidebar.examples")}</div>
          <div className="text-sm">{examplesCount}</div>
        </div>
        <div className="pt-2">
          <PrevNextButtons slug={slug} prevId={prevId} nextId={nextId} t={t} />
        </div>
        <Button asChild variant="ghost" size="sm" className="w-full">
          <Link href={`/course/${slug}`}>{t("course.idiomDetail.sidebar.backToList")}</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function LockedCourseState({
  slug,
  numericId,
  cleanedTitle,
  t,
  onShowDialog,
}: {
  slug: string;
  numericId: number;
  cleanedTitle: string;
  t: (key: string, fallback?: string) => string;
  onShowDialog: () => void;
}) {
  const heading = t("course.freePlanLimit.lockedHeading", "Upgrade to continue learning");
  const description = t(
    "course.freePlanLimit.lockedMessage",
    "Free plan users can access the first 10 idioms. Upgrade to unlock the full course, including lesson #{number}."
  ).replace("{number}", String(numericId));

  return (
    <div className="flex min-h-[360px] flex-col items-center justify-center rounded-2xl border border-dashed border-border/60 bg-muted/20 p-10 text-center">
      <div className="flex size-20 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Lock className="size-8" />
      </div>
      <h2 className="mt-6 text-xl font-semibold">{heading}</h2>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">{description}</p>
      {cleanedTitle ? (
        <p className="mt-1 text-sm font-medium text-foreground">#{numericId} · {cleanedTitle}</p>
      ) : null}
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Button onClick={onShowDialog} className="gap-2">
          <Crown className="size-4" />
          {t("user.actions.upgrade", "Upgrade now")}
        </Button>
        <Button asChild variant="outline" className="gap-2">
          <Link href={`/course/${slug}`}>
            {t("course.freePlanLimit.backToList", "Back to course overview")}
          </Link>
        </Button>
      </div>
    </div>
  );
}

function MainContent({
  slug,
  numericId,
  cleanedTitle,
  data,
  examples,
  prevId,
  nextId,
  t,
}: {
  slug: string;
  numericId: number;
  cleanedTitle: string;
  data: Record<string, any>;
  examples: Example[];
  prevId: number | null;
  nextId: number | null;
  t: (key: string, fallback?: string) => string;
}) {
  return (
    <Card className="border bg-card order-1 lg:order-2 rounded-2xl">
      <CardContent className="p-5 rounded-2xl space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs text-muted-foreground mb-1"># {numericId}</div>
            <h1 className="text-2xl font-semibold mb-2">
              {numericId}. {cleanedTitle || t("course.idiomDetail.untitled", "Untitled")}
            </h1>
          </div>
          <div className="hidden sm:flex gap-2">
            <PrevNextButtons slug={slug} prevId={prevId} nextId={nextId} compact t={t} />
        </div>
        </div>

        {data?.englishExplanation && (
          <p className="mt-4 mb-3 whitespace-pre-line">{data.englishExplanation}</p>
        )}

        {data?.chineseMeaning && (
          <details className="mb-4">
            <summary className="cursor-pointer text-sm text-muted-foreground">{t("course.idiomDetail.translationToggle")}</summary>
            <div className="mt-2 text-sm whitespace-pre-line">{data.chineseMeaning}</div>
          </details>
        )}

        {examples.length > 0 ? (
          <ExamplesList
            slug={slug}
            numericId={numericId}
            idiomTitle={cleanedTitle || `#${numericId}`}
            examples={examples}
            t={t}
          />
        ) : (
          <NoExamples t={t} />
        )}
      </CardContent>
    </Card>
  );
}

function PrevNextButtons({
  slug,
  prevId,
  nextId,
  compact = false,
  t,
}: {
  slug: string;
  prevId: number | null;
  nextId: number | null;
  compact?: boolean;
  t: (key: string, fallback?: string) => string;
}) {
  const sharedClasses = compact ? "px-2 py-1 text-sm" : "px-3 py-1 text-sm";

  return (
    <div className={`flex ${compact ? "gap-2" : "gap-2"}`}>
      <Button
        asChild
        variant="outline"
        size="sm"
        className={sharedClasses}
        disabled={!prevId}
      >
        <Link href={prevId ? `/course/${slug}/${prevId}` : "#"}>
          <ArrowLeft className="h-4 w-4 mr-1" /> {t("course.common.previous")}
        </Link>
      </Button>
      <Button
        asChild
        variant="outline"
        size="sm"
        className={sharedClasses}
        disabled={!nextId}
      >
        <Link href={nextId ? `/course/${slug}/${nextId}` : "#"}>
          {t("course.common.next")} <ArrowRight className="h-4 w-4 ml-1" />
        </Link>
      </Button>
    </div>
  );
}

function ExamplesList({
  slug,
  numericId,
  idiomTitle,
  examples,
  t,
}: {
  slug: string;
  numericId: number;
  idiomTitle: string;
  examples: Example[];
  t: (key: string, fallback?: string) => string;
}) {
  const { user } = useUser();
  const [idiomProgress, setIdiomProgress] = useState<Record<string, string> | null>(null);
  const [loadingProgress, setLoadingProgress] = useState(false);

  useEffect(() => {
    if (!user?.id) {
      setIdiomProgress(null);
      setLoadingProgress(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      if (cancelled) return;
      setLoadingProgress(true);
      try {
        const url = `/api/course/progress?userId=${encodeURIComponent(user.id)}&slug=${encodeURIComponent(slug)}&idiomId=${numericId}`;
        const res = await fetch(url, { cache: "no-store" });
        const json = await res.json();
        if (!cancelled) {
          const idiomFields = (json?.idiom ?? {}) as Record<string, string>;
          setIdiomProgress(idiomFields);
        }
      } catch (error) {
        if (!cancelled) {
          setIdiomProgress({});
        }
      } finally {
        if (!cancelled) setLoadingProgress(false);
      }
    };

    load();

    const handleRecordSaved = () => {
      // avoid clearing existing progress to prevent UI flicker
      load();
    };

    window.addEventListener("shadowingRecordSaved", handleRecordSaved as EventListener);

    return () => {
      cancelled = true;
      window.removeEventListener("shadowingRecordSaved", handleRecordSaved as EventListener);
    };
  }, [user?.id, slug, numericId]);

  const mask = useMemo(() => {
    if (!idiomProgress) return "";
    return String(idiomProgress[`i:${numericId}:mask`] || "");
  }, [idiomProgress, numericId]);

  const exampleDone = (sequence: number): boolean | null => {
    if (!idiomProgress) return null;
    const fieldKey = `i:${numericId}:ex:${sequence}`;
    const exVal = idiomProgress[fieldKey];
    if (String(exVal) === "1") return true;
    if (mask) {
      const idx = Math.max(0, sequence - 1);
      return mask.charAt(idx) === "1";
    }
    return false;
  };

  return (
    <div>
      <h2 className="text-lg font-medium mb-2">{t("course.idiomDetail.examples.title")}</h2>
      <p className="text-sm text-muted-foreground mb-3">
        {t("course.idiomDetail.examples.description")}
      </p>
      <ol className="space-y-4">
        {examples.map((ex, idx) => (
          <li key={`${ex.sequence ?? idx}-${ex.english}`} className="rounded-xl border p-4 bg-background">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline">#{ex?.sequence ?? idx + 1}</Badge>
                <ExampleCompletionIndicator
                  done={exampleDone(ex?.sequence ?? idx + 1)}
                  loading={idiomProgress === null && loadingProgress}
                />
              </div>
              <ExampleShadowButton
                example={{ english: ex.english, chinese: ex.chinese, audioUrl: ex.audioUrl }}
                exampleSequence={ex?.sequence ?? idx + 1}
                courseSlug={slug}
                idiomId={numericId}
                idiomTitle={idiomTitle}
              />
            </div>
            <div className="mb-2 prose prose-sm dark:prose-invert max-w-none prose-em:font-semibold prose-em:text-primary">
              <ReactMarkdown>{ex.english}</ReactMarkdown>
            </div>
            {ex.chinese && (
              <details className="mb-2 text-sm">
                <summary className="cursor-pointer text-muted-foreground">{t("course.idiomDetail.translationToggle")}</summary>
                <div className="mt-1 whitespace-pre-line">{ex.chinese}</div>
              </details>
            )}
            {ex.audioUrl ? <AudioWave url={ex.audioUrl} dark={false} autoPlay={false} /> : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

function NoExamples({ t }: { t: (key: string, fallback?: string) => string }) {
  return (
    <div className="text-center p-8 border border-dashed rounded-xl text-muted-foreground">
      <Mic className="mx-auto mb-2 h-10 w-10 opacity-50" />
      <p>{t("course.idiomDetail.examples.empty")}</p>
    </div>
  );
}

function EmptyState({ t }: { t: (key: string) => string }) {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="text-center space-y-4">
        <h2 className="text-2xl font-semibold">{t("common.loading")}</h2>
        <p className="text-muted-foreground">
          {t("course.idiomDetail.errors.load")}
        </p>
      </div>
    </div>
  );
}
